import React, {useContext, useMemo, useState} from 'react';
import {Card, IOTTableWrapper, PrimaryButton, DeleteDialog, useSnackbar} from '@Iot-Bee/standard-web-library';

import SchedulingDialog from './components/SchedulingDialog';
import {CalenderSchedule, DeviceNameAndUsed, EditSchedule, Schedule, ServerDevice} from '../../Types/types';
import {noCacheFetcher} from '../../utils/fetch';
import {useCustomSWR} from '../../hooks/customSWR';
import {DevicesContext} from '../../context/DevicesContext';
import {columns} from './Utils/util';
import {backendScheduleToCalenderSchedule} from './Utils/util';

const Scheduling = () => {
	const devicesContext = useContext(DevicesContext);

	if (devicesContext === undefined) {
		throw new Error('DevicesContext is undefined');
	}

	const {devices} = devicesContext;
	const {data: rawSchedules, isLoading, mutate: refreshSchedules} = useCustomSWR<Schedule[]>(process.env.REACT_APP_API_URL + 'schedules');

	const devicesAndUsed = useMemo(() => {
		if (!devices || !rawSchedules) return [];

		return [
			{name: 'Select all free devices', isUsed: '', selectAll: true},
			...devices.map((device) => {
				const isUsed = rawSchedules.find((schedule) => schedule.devices.find((d) => d.id === device.id || d.friendlyName === device.deviceName));
				return {name: device.deviceName, isUsed: isUsed ? isUsed.name : ''};
			}),
		];
	}, [devices, rawSchedules]);

	const displaySchedules = useMemo(() => {
		if (!rawSchedules) return [];
		return rawSchedules.map((schedule) => {
			const calenderSchedule = backendScheduleToCalenderSchedule(schedule);
			calenderSchedule.allDevices = schedule.devices
				.map((device: ServerDevice) => {
					return device.friendlyName ? device.friendlyName : device.id;
				})
				.join(', ');
			if (calenderSchedule.allDevices.length > 50) {
				calenderSchedule.allDevices = calenderSchedule.allDevices.substring(0, 50) + '...';
			}
			return calenderSchedule;
		});
	}, [rawSchedules]);

	const [dialogInfo, setDialogInfo] = useState<{open: boolean; mode: 'add' | 'edit'; eventToEdit: CalenderSchedule | null}>({
		open: false,
		mode: 'add',
		eventToEdit: null,
	});

	const [deleteDialog, setDeleteDialog] = useState<{open: boolean; schedule: CalenderSchedule | undefined}>({open: false, schedule: undefined});

	const {
		actions: {openSnackbar},
	} = useSnackbar();

	const resetDialogInfo = (open: boolean) => {
		setDialogInfo({
			open: open,
			mode: 'add',
			eventToEdit: null,
		});
	};

	const handleAddSchedule = (schedule: EditSchedule) => {
		const devices = schedule.devices.map((device: DeviceNameAndUsed) => {
			return device.name;
		});

		const body: Omit<CalenderSchedule, 'id' | 'allDevices'> = {
			name: schedule.name,
			scheduleEvents: schedule.scheduleEvents,
			devices: devices,
			temperatures: schedule.temperatures,
		};

		noCacheFetcher(process.env.REACT_APP_API_URL + 'schedule', {method: 'POST', body: JSON.stringify(body)})
			.then((res) => {
				if (res.status === 200) {
					refreshSchedules();
					openSnackbar('success', 'Schedule added successfully!');
					resetDialogInfo(false);
				} else {
					openSnackbar('error', 'Failed to add schedule');
				}
			})
			.catch((error) => {
				console.error(error);
				openSnackbar('error', 'Failed to add schedule');
			});
	};

	const handleDelete = (schedule: CalenderSchedule) => {
		noCacheFetcher(`${process.env.REACT_APP_API_URL}schedule/${schedule.id}`, {method: 'DELETE'})
			.then((res) => {
				if (res.status === 200) {
					refreshSchedules();
					openSnackbar('success', 'Schedule deleted successfully!');
				} else {
					openSnackbar('error', 'Failed to delete schedule');
				}
			})
			.catch((error) => {
				console.error(error);
				openSnackbar('error', 'Failed to delete schedule');
			});
	};

	const handleUpdateSchedule = (schedule: EditSchedule) => {
		const mappedDevices = schedule.devices.map((device: DeviceNameAndUsed) => {
			return device.name;
		});

		const calenderSchedule: CalenderSchedule = {
			id: schedule.id,
			name: schedule.name,
			scheduleEvents: schedule.scheduleEvents,
			devices: mappedDevices,
			allDevices: '',
			temperatures: schedule.temperatures,
		};

		calenderSchedule.allDevices = calenderSchedule.devices.join(', ');
		if (calenderSchedule.allDevices.length > 50) {
			calenderSchedule.allDevices = calenderSchedule.allDevices.substring(0, 50) + '...';
		}

		noCacheFetcher(`${process.env.REACT_APP_API_URL}schedule/${schedule.id}`, {
			method: 'PUT',
			body: JSON.stringify({
				name: schedule.name,
				scheduleEvents: schedule.scheduleEvents,
				devices: mappedDevices,
				temperatures: schedule.temperatures,
			}),
		})
			.then((res) => {
				if (res.status === 200) {
					refreshSchedules();
					resetDialogInfo(false);
					openSnackbar('success', 'Schedule updated successfully!');
				} else {
					openSnackbar('error', 'Failed to update schedule');
				}
			})
			.catch((error) => {
				console.error(error);
				openSnackbar('error', 'Failed to update schedule');
			});
	};

	return (
		<Card
			title="Scheduling"
			headerActions={
				<PrimaryButton
					onClick={() => {
						resetDialogInfo(true);
					}}>
					Add new schedule
				</PrimaryButton>
			}>
			<IOTTableWrapper
				columns={columns}
				data={displaySchedules ?? []}
				onEdit={(eventItem) => {
					setDialogInfo({open: true, mode: 'edit', eventToEdit: eventItem});
				}}
				onDelete={(itemToDelete) => {
					setDeleteDialog({open: true, schedule: itemToDelete});
				}}
				isLoading={isLoading}
			/>
			<SchedulingDialog
				schedule={dialogInfo.eventToEdit}
				open={dialogInfo.open}
				allClientDevices={devicesAndUsed}
				onClose={() => {
					resetDialogInfo(false);
				}}
				onSave={(schedule) => {
					if (dialogInfo.mode === 'edit') {
						handleUpdateSchedule(schedule);
					} else {
						handleAddSchedule(schedule);
					}
				}}
			/>
			<DeleteDialog
				title={deleteDialog.schedule?.name || ''}
				open={deleteDialog.open}
				handleDelete={() => {
					if (!deleteDialog.schedule) return;
					handleDelete(deleteDialog.schedule);
					setDeleteDialog((prev) => ({...prev, open: false}));
				}}
				onClose={() => setDeleteDialog((prev) => ({...prev, open: false}))}
				itemName={deleteDialog.schedule?.name || ''}
			/>
		</Card>
	);
};

export default Scheduling;
